import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Music, Send, ArrowLeft, Loader2, DollarSign } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const PREMIUM_PITCH_COST = 1500; // $15.00 in cents

const PremiumPitch = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userCredits, setUserCredits] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    spotifyUrl: '',
    artistName: '',
    email: '',
    additionalInfo: ''
  });

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate('/auth');
        return;
      }

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      if (error) {
        console.error('Error loading profile:', error);
        navigate('/auth');
        return;
      }

      if (profile) {
        setCurrentUser(profile);
        setUserCredits(profile.premium_credits || 0);
        // Pre-fill email and artist name if available
        if (profile.email) {
          setFormData(prev => ({ ...prev, email: profile.email }));
        }
        if (profile.artist_name) {
          setFormData(prev => ({ ...prev, artistName: profile.artist_name }));
        }
      }
    } catch (error) {
      console.error('Error loading user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const validateSpotifyUrl = (url) => {
    const spotifyPattern = /^https:\/\/open\.spotify\.com\/track\/[a-zA-Z0-9]+(\?.*)?$/;
    return spotifyPattern.test(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.spotifyUrl.trim()) {
      toast.error('Please enter your Spotify track URL');
      return;
    }

    if (!validateSpotifyUrl(formData.spotifyUrl.trim())) {
      toast.error('Please enter a valid Spotify track URL (e.g., https://open.spotify.com/track/...)');
      return;
    }

    if (!formData.artistName.trim()) {
      toast.error('Please enter your artist name');
      return;
    }

    if (!formData.email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    if (userCredits < PREMIUM_PITCH_COST) {
      toast.error(`Insufficient credits. You need $15.00 but have $${(userCredits / 100).toFixed(2)}`);
      navigate('/BuyCredits');
      return;
    }

    setIsSubmitting(true);

    try {
      // Deduct credits from user's premium_credits
      const newBalance = userCredits - PREMIUM_PITCH_COST;
      
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          premium_credits: newBalance,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentUser.id);

      if (updateError) {
        throw new Error('Failed to process payment');
      }

      // Create premium pitch submission record for admin tracking
      const { error: submissionError } = await supabase
        .from('premium_pitch_submissions')
        .insert({
          user_id: currentUser.id,
          spotify_url: formData.spotifyUrl.trim(),
          artist_name: formData.artistName.trim(),
          email: formData.email.trim(),
          additional_info: formData.additionalInfo.trim() || null,
          status: 'pending'
        });

      if (submissionError) {
        console.error('Submission record error:', submissionError);
        // Don't fail - credits already deducted
      }

      // Log the transaction
      const { error: transactionError } = await supabase
        .from('credit_transactions')
        .insert({
          user_id: currentUser.id,
          transaction_type: 'premium_pitch',
          amount: PREMIUM_PITCH_COST / 100,
          credits_added: -PREMIUM_PITCH_COST,
          balance_before: userCredits,
          balance_after: newBalance,
          metadata: {
            spotify_url: formData.spotifyUrl.trim(),
            artist_name: formData.artistName.trim(),
            email: formData.email.trim(),
            additional_info: formData.additionalInfo.trim()
          }
        });

      if (transactionError) {
        console.error('Transaction log error:', transactionError);
      }

      // Emit credits updated event
      window.dispatchEvent(new CustomEvent('credits:updated'));
      window.dispatchEvent(new CustomEvent('earnedCreditsUpdated'));

      toast.success('Premium pitch submitted! We\'ll pitch your track to curators.');
      navigate('/profile');

    } catch (error) {
      console.error('Premium pitch error:', error);
      toast.error(error.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasEnoughCredits = userCredits >= PREMIUM_PITCH_COST;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
        <Loader2 className="w-8 h-8 animate-spin text-[#a0a0a0]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/submit-options')}
          className="mb-6 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Card className="border-primary/20 bg-card/50 backdrop-blur">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center mb-4">
              <Music className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl md:text-3xl font-bold">
              Premium Pitch Service
            </CardTitle>
            <CardDescription className="text-lg">
              We'll pitch your Spotify track directly to our network of curators
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Cost Display */}
            <div className="bg-muted/50 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-500" />
                <span className="font-medium">Service Cost:</span>
              </div>
              <span className="text-xl font-bold text-green-500">$15.00</span>
            </div>

            {/* Credits Balance */}
            <div className={`rounded-lg p-4 flex items-center justify-between ${hasEnoughCredits ? 'bg-green-500/10 border border-green-500/20' : 'bg-destructive/10 border border-destructive/20'}`}>
              <span className="font-medium">Your Balance:</span>
              <span className={`text-xl font-bold ${hasEnoughCredits ? 'text-green-500' : 'text-destructive'}`}>
                ${(userCredits / 100).toFixed(2)}
              </span>
            </div>

            {!hasEnoughCredits && (
              <Button 
                onClick={() => navigate('/BuyCredits')} 
                className="w-full bg-gradient-to-r from-green-500 to-green-600"
              >
                Buy Credits
              </Button>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="spotifyUrl" className="text-sm font-medium">
                  Spotify Track URL *
                </Label>
                <Input
                  id="spotifyUrl"
                  type="url"
                  placeholder="https://open.spotify.com/track/..."
                  value={formData.spotifyUrl}
                  onChange={(e) => setFormData({ ...formData, spotifyUrl: e.target.value })}
                  className="bg-background/50"
                  disabled={isSubmitting}
                />
                <p className="text-xs text-muted-foreground">
                  Paste your Spotify track link here
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="artistName" className="text-sm font-medium">
                  Artist Name *
                </Label>
                <Input
                  id="artistName"
                  type="text"
                  placeholder="Your artist/band name"
                  value={formData.artistName}
                  onChange={(e) => setFormData({ ...formData, artistName: e.target.value })}
                  className="bg-background/50"
                  disabled={isSubmitting}
                  maxLength={100}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Contact Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-background/50"
                  disabled={isSubmitting}
                  maxLength={255}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="additionalInfo" className="text-sm font-medium">
                  Additional Info (Optional)
                </Label>
                <Textarea
                  id="additionalInfo"
                  placeholder="Any additional information about your track, genre preferences, target playlists, etc."
                  value={formData.additionalInfo}
                  onChange={(e) => setFormData({ ...formData, additionalInfo: e.target.value })}
                  className="bg-background/50 min-h-[100px]"
                  disabled={isSubmitting}
                  maxLength={1000}
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || !hasEnoughCredits}
                className="w-full h-12 text-lg bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Submit for $15.00
                  </>
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground">
              Our team will personally pitch your track to relevant playlist curators within 48 hours.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PremiumPitch;
